import { useMemo } from 'react';
import { request } from 'graphql-request';
import useGraphqlRequestParams from '../../../hooks/useGraphqlRequestParams';
import type { FilterItem, UrlFilters } from '../../../hooks/useUrlFilters';
import { LINEAGE_GRAPH } from '../../../constants/queryKeys';
import type { Filter } from '../../../services/gql/types';
import { hashArray } from '../../../utils/hashing';
import { getLineageGraphQuery } from '../api';
import { LINEAGE_PER_PARENT_COLUMNS_LIMIT } from '../constants';
import type {
  HopLimit,
  LineageGraphResponse,
  LineageGraphSource,
} from '../types';

const LINEAGE_FILTER_OPERATORS = new Set([
  'equals',
  'notEquals',
  'contains',
  'notContains',
]);

const isLineageNodeFilter = (filter: FilterItem): filter is Filter =>
  LINEAGE_FILTER_OPERATORS.has(filter.operator);

const getLineageNodeFilters = (filters?: UrlFilters): Filter[] =>
  (filters ?? []).filter(isLineageNodeFilter);

type Props = {
  assetIds?: string[];
  columnIds?: string[];
  filters?: UrlFilters;
  hopLimit?: HopLimit;
};

const useLineageGraphSource = ({
  assetIds,
  columnIds,
  filters,
  hopLimit,
}: Props): LineageGraphSource | undefined => {
  const { accessToken, environmentId, gqlEndpoint } = useGraphqlRequestParams(
    {},
  );

  return useMemo(() => {
    if (!accessToken || !environmentId) return undefined;

    const hasColumnFilter = !!columnIds?.length;
    const upstreamHops = hopLimit?.upstream ?? 1;
    const downstreamHops = hopLimit?.downstream ?? 1;
    const nodeFilters = getLineageNodeFilters(filters);
    const selectedNodeIds = [...(assetIds ?? []), ...(columnIds ?? [])];

    return {
      queryKey: [
        LINEAGE_GRAPH,
        environmentId,
        selectedNodeIds.length ? hashArray(selectedNodeIds) : '',
        upstreamHops,
        downstreamHops,
        nodeFilters,
      ],
      fetchGraph: async (view) => {
        const data = await request<{ lineageGraph: LineageGraphResponse }>(
          gqlEndpoint,
          getLineageGraphQuery,
          {
            filters: {
              assetIds: assetIds ?? [],
              columnIds: hasColumnFilter ? columnIds : null,
              upstreamHops,
              downstreamHops,
              perParentColumnsLimit: hasColumnFilter
                ? LINEAGE_PER_PARENT_COLUMNS_LIMIT
                : null,
            },
            nodeFilters,
            view,
          },
          { authorization: `Bearer ${accessToken}` },
        );
        return data.lineageGraph;
      },
    };
  }, [
    accessToken,
    environmentId,
    gqlEndpoint,
    assetIds,
    columnIds,
    filters,
    hopLimit?.upstream,
    hopLimit?.downstream,
  ]);
};

export default useLineageGraphSource;
